import { transformDateForDrugs } from "../pages/utils";
import InteractionsChip from "./InteractionsChip";
import { calculateDdiColor } from "./utils";
import { useTranslation } from "react-i18next";
import { useContext, useEffect, useState } from "react";
import RiskInfo from "./RiskInfo";
import InitContext from "../pages/InitContext";
import useMmcService from "../../services/MmcService";
import { Consts } from "../pages/consts";

const DrugsAccordionHeaderContent = ({ drug, onDeleteDrug, patDrugs }) => {
  const { t: t } = useTranslation();
  const [showRisk, setShowRisk] = useState(false);
  const [dosageReport, setDosageReport] = useState(null);
  const [stopDialogOpened, setStopDialogOpened] = useState(false);
  const [stopDate, setStopDate] = useState("");
  const context = useContext(InitContext);
  const {clearError, getDosageRecommendationReport, getDosageRecommendationReportFile} = useMmcService();

  useEffect(() => {
    clearError();

    getDosageRecommendationReport(context?.token, drug?.id).then((report) => {
      if(report?.message === "no model found for this medicine"){
        setDosageReport({...report, message2: t("no_model_found")});
      } else {
        setDosageReport(report)
      }
    });
  }, []);

  let relativeRisk = +drug?.ddgi?.relative_risk_change;
  let riskChangeText;

  if (!drug?.ddgi) {
    riskChangeText = Consts.NOT_AVAILABLE;
  } else if (relativeRisk === -1) {
    riskChangeText = "see_explanation";
  } else if(relativeRisk === 0){
    riskChangeText = "is_not_increased";
  } else {
    relativeRisk = (Math.round(relativeRisk * 100 * 100) / 100).toFixed(2);

    const decrOrIncr = relativeRisk < 0
      ? t("has_decreased_by")
      : t("has_increased_by");

    riskChangeText =  decrOrIncr + " " + Math.abs(relativeRisk) + "%";
  }

  const isStandardRiskChangeText =  riskChangeText === "see_explanation"
    || riskChangeText === "is_not_increased" || riskChangeText === Consts.NOT_AVAILABLE;

  const handleReportDownload = async (prescriptionId) => {
    const result = await getDosageRecommendationReportFile(context?.token, prescriptionId);

    if (result) {
      const { blob, filename } = result;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body?.appendChild(a);
      a.click();
      a.remove();
    }
  };

  const sortedDdis = drug?.ddis?.sort((a, b) => {
    if (a.ddiDrug?.activeSubstance < b.ddiDrug?.activeSubstance) {
      return -1;
    }

    if (a.ddiDrug?.activeSubstance > b.ddiDrug?.activeSubstance) {
      return 1;
    }

    return 0;
  });

  let substance = drug?.activeSubstance === Consts.ANY
    ? t(Consts.NOT_SPECIFIED)?.toLowerCase()
    : drug?.activeSubstance;

  const handleStopDate = () => {
    setStopDialogOpened(false);
    onDeleteDrug(drug, stopDate);
  }

  return (
    <div
      className="w3-row w3-padding-small accordion-header-row"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="w3-col s2 w3-mobile">
        <label className="bold-label w3-block">
          {drug?.productName === Consts.NOT_SPECIFIED || drug?.productName === Consts.ANY
            ? t(Consts.NOT_SPECIFIED)?.toLowerCase()
            : drug?.productName}
        </label>
        {drug?.activeSubstance && (
          <>
            <label className="w3-block">{`(${t("active_substance")}: `}</label>
            <label>{`${substance?.replaceAll("&&&", ", ")})`}</label>
          </>
        )}
        <label className="w3-block">
          {`${t("start_date")}: ${transformDateForDrugs(drug?.startDate)}`}
        </label>
      </div>
      { stopDialogOpened && (
        <div className="feedback-modal">
          <div className="print-modal-content">
            <header className="feedback-modal-header">
              <h2>{t("stop_drug_date")}</h2>
            </header>
            <div>
              <input
                type="date"
                value={stopDate}
                className="w3-input w3-border w3-round form-input"
                onChange={(e) => setStopDate(e.target.value)}
              />
            </div>
            <div className="feedback-modal-buttons">
              <button onClick={handleStopDate} className="w3-btn w3-round-xlarge w3-theme-d5">
                {t("save")}
              </button>
              <button onClick={() => setStopDialogOpened(false)} className="w3-btn w3-round-xlarge w3-theme-d5">
                {t("cancel")}
              </button>
            </div>
          </div>
        </div>
      ) }
      <div
        className={
          context?.role === Consts.PATIENT
            ? "w3-col s8 w3-mobile"
            : "w3-col s4 w3-mobile"
        }
      >
        <div className="w3-container">
          <div className="w3-border w3-center w3-round-large w3-padding-small">
            <label className="bold-label">{t("interactions_between")}</label>
            <div className="w3-row">
              <InteractionsChip
                label={t("ddi")}
                title={t("drug_drug_interactions")}
                color={
                  patDrugs?.length === 1
                    ? Consts.GREY
                    : calculateDdiColor(sortedDdis, context?.role)
                }
                drug={drug}
              />
              <InteractionsChip
                label={t("dgi")}
                title={t("drug_gene_interactions")}
                color={drug?.dgis?.length > 0 ? "#1b7a9f" : Consts.GREY}
                drug={drug}
              />
            </div>
          </div>
        </div>
      </div>
      {context?.role !== Consts.PATIENT && (
        <>
          <div className="w3-col s2 w3-mobile">
            <div className="w3-container">
              <div className="w3-border w3-center w3-round-large w3-padding-small">
                <label className="bold-label">{t("adverse_event_risks")}</label>
                <label
                  className={drug?.ddgi ? "underlined-text" : "not_underlined-text" }
                  onClick={drug?.ddgi ? () => setShowRisk(true) : undefined}
                >
                  {isStandardRiskChangeText ? t(riskChangeText) : riskChangeText}
                </label>
                <div className="w3-half w3-padding-small">
                  <RiskInfo
                    showRisk={showRisk}
                    setShowRisk={setShowRisk}
                    drug={drug}
                  />
                </div>
              </div>
            </div>
          </div>
          <div className="w3-col s2 w3-mobile w3-center">
            <div className="w3-container">
              <div className="w3-border w3-center w3-round-large w3-padding-small">
                <label className="bold-label">{t("dosage_recommendation")}</label>
                {dosageReport?.reportAvailable && (
                  <label
                    className="label-link"
                    onClick={() =>
                      handleReportDownload(dosageReport?.prescriptionId)
                    }
                  >
                    {t("report_for")} {drug?.productName} {new Date(dosageReport?.date).toLocaleDateString()}
                  </label>
                )}
                {!dosageReport?.reportAvailable && dosageReport?.orderId && dosageReport?.message && (
                  <label>{t("requested")}</label>
                )}
                {!dosageReport?.reportAvailable && !dosageReport?.orderId && dosageReport?.message && (
                    <label>{dosageReport?.message2}</label>
                )}
                {!dosageReport?.reportAvailable &&
                  !dosageReport?.message &&
                  dosageReport?.id && <label>{t("requested")}</label>}
                {!dosageReport?.id && <label>{t("not_requested")}</label>}
              </div>
            </div>
          </div>
        </>
      )}
      <div className="w3-col s1 w3-right">
        <button
          type="button"
          className="w3-btn w3-round-xlarge w3-right w3-theme-d5 w3-margin-top"
          onClick={() => setStopDialogOpened(true)}
        >
          {t("stop_drug")}
        </button>
      </div>
    </div>
  );
};

export default DrugsAccordionHeaderContent;
